import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { TimelineStayGateway } from '@wilson/api/gateway';
import { FeaturePurePipe } from '@wilson/authorization';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import {
  FeatureName,
  Stay,
  StayStatus,
  TimelineTimeframe,
} from '@wilson/interfaces';
import { WilsonState } from '@wilson/non-domain-specific/decorators/wilson-state';
import { getTimeZoneCorrectedDateRange } from '@wilson/utils';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  Observable,
  combineLatest,
  firstValueFrom,
  map,
  shareReplay,
  tap,
} from 'rxjs';
import { ShiftId, StayId } from '../interfaces';
import { ShiftTimelineSettingsState } from '../shift-timeline-settings/shift-timeline-settings.state';
import { TimelineUpdateUserStays } from './shift-timeline-data.actions';
import { ShiftTimelineDataState } from './shift-timeline-data.state';
import { AssignedRegionVisibilityStateService } from './shift-timeline-visibility-state.service';

@Injectable()
export class TimelineStaysSuggestionService {
  private readonly store = inject(Store);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private assignedRegionVisibilityStateService = inject(
    AssignedRegionVisibilityStateService,
  );
  private readonly featureFlagPurePipe = inject(FeatureFlagPurePipe);
  private readonly featurePurePipe = inject(FeaturePurePipe);
  protected timelineStayGateway = inject(TimelineStayGateway);

  @WilsonState<Record<ShiftId, boolean> | null>(null)
  private shiftsRecommendedStaysInRangeMap!: Record<StayId, boolean> | null;
  shiftsRecommendedStaysInRangeMap$!: Observable<Record<StayId, boolean>>;

  @WilsonState<Record<StayId, boolean> | null>(null)
  private validatedStaysInRangeMap!: Record<StayId, boolean> | null;
  validatedStaysInRangeMap$!: Observable<Record<StayId, boolean> | null>;

  @WilsonState<number | null>(null)
  private refreshKPI!: number | null;
  refreshKPI$!: Observable<number | null>;

  public readonly canRenderStays$: Observable<boolean> = combineLatest([
    this.featureFlagPurePipe.transform('timeline-stays-management'),
    this.featurePurePipe.transform(FeatureName.StayManagement),
    this.store.select(ShiftTimelineSettingsState.areStaysVisible),
  ]).pipe(
    map(
      ([timelineFlag, stayManagementFlag, areStaysVisible]) =>
        timelineFlag && stayManagementFlag && areStaysVisible,
    ),
    shareReplay(1),
  );

  private refreshStayKPICount(): void {
    this.refreshKPI = Math.random();
  }

  public resetRecommendedStaysInRangeMap(): void {
    this.shiftsRecommendedStaysInRangeMap = null;
  }

  async refreshStaysDataForSpecifiedUsers(userIds: string[]): Promise<void> {
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );

    if (await firstValueFrom(this.canRenderStays$)) {
      firstValueFrom(
        this.updateSingleUserNecessaryStaysInRange({
          dateRange: visibleDateRange,
          userIds: userIds,
        }),
      );

      firstValueFrom(
        this.refreshUsersValidatedStays({
          dateRange: visibleDateRange,
          userIds: userIds,
        }),
      );
      this.refreshStayKPICount();
    }
  }

  private refreshUsersValidatedStays({
    dateRange,
    userIds,
  }: {
    dateRange: TimelineTimeframe;
    userIds: string[];
  }): Observable<Stay[]> {
    try {
      const correctedInterval = getTimeZoneCorrectedDateRange(dateRange);
      return this.timelineStayGateway
        .getStays({
          startDatetime: correctedInterval.start.toISOString(),
          endDatetime: correctedInterval.end.toISOString(),
          statuses: [
            StayStatus.Open,
            StayStatus.Requested,
            StayStatus.Confirmed,
            StayStatus.Invoiced,
          ],
          userIds,
        })
        .pipe(
          tap((stays) => {
            if (stays.length) {
              const stayIds: string[] = stays.map((stay) => stay.id);
              firstValueFrom(this.updateUsersValidatedStays(stayIds));
            }
          }),
        );
    } catch (error) {
      console.error(
        'An error occurred while refreshing user stays validations in range:',
        error,
      );

      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
  }

  async refreshDeterminedStaysData(): Promise<void> {
    const visibleDateRange = this.store.selectSnapshot(
      ShiftTimelineDataState.visibleDateRange,
    );
    const visibleUserIdsSet = await firstValueFrom(
      this.assignedRegionVisibilityStateService.visibleUserIdsSet$,
    );

    this.refreshStayKPICount();
    return firstValueFrom(
      this.fetchNecessaryStaysInRange({
        dateRange: visibleDateRange,
        userIds: Array.from(visibleUserIdsSet),
      }),
    ).then(() => {
      firstValueFrom(
        this.fetchValidateAndStoreUserStays({
          dateRange: visibleDateRange,
          userIds: Array.from(visibleUserIdsSet),
        }),
      );
    });
  }

  private getNecessaryStaysForDateRange({
    dateRange,
    userIds,
  }: {
    dateRange: TimelineTimeframe;
    userIds: string[];
  }): Observable<Record<string, boolean>> {
    const correctedInterval = getTimeZoneCorrectedDateRange(dateRange);

    return this.timelineStayGateway.fetchNecessaryStaysInRange({
      dateRange: {
        start: correctedInterval.start,
        end: correctedInterval.end,
      },
      userIds: userIds,
    });
  }

  public updateSingleUserNecessaryStaysInRange({
    dateRange,
    userIds,
  }: {
    dateRange: TimelineTimeframe;
    userIds: string[];
  }): Observable<Record<string, boolean>> {
    try {
      return this.getNecessaryStaysForDateRange({
        dateRange,
        userIds,
      }).pipe(
        tap((response) => {
          const duplicatedRecommendedStaysInRange =
            this.shiftsRecommendedStaysInRangeMap;
          if (duplicatedRecommendedStaysInRange) {
            Object.entries(response).map(
              ([key, value]) =>
                (duplicatedRecommendedStaysInRange[key] = value),
            );
          }
          this.shiftsRecommendedStaysInRangeMap = {
            ...duplicatedRecommendedStaysInRange,
          };
        }),
      );
    } catch (error) {
      console.error(
        'An error occurred while updating user determining necessary stays in range:',
        error,
      );
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
  }

  public fetchNecessaryStaysInRange({
    dateRange,
    userIds,
  }: {
    dateRange: TimelineTimeframe;
    userIds: string[];
  }): Observable<Record<string, boolean>> {
    try {
      return this.getNecessaryStaysForDateRange({
        dateRange,
        userIds,
      }).pipe(
        tap((response) => {
          this.shiftsRecommendedStaysInRangeMap = response;
        }),
      );
    } catch (error) {
      console.error(
        'An error occurred while determining necessary stays in range:',
        error,
      );
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
  }

  public fetchValidateAndStoreUserStays({
    dateRange,
    userIds,
  }: {
    dateRange: TimelineTimeframe;
    userIds: string[];
  }): Observable<Stay[]> {
    try {
      const correctedInterval = getTimeZoneCorrectedDateRange(dateRange);
      return this.timelineStayGateway
        .getStays({
          startDatetime: new Date(correctedInterval.start).toISOString(),
          endDatetime: new Date(correctedInterval.end).toISOString(),
          statuses: [
            StayStatus.Open,
            StayStatus.Requested,
            StayStatus.Confirmed,
            StayStatus.Invoiced,
          ],
          userIds,
        })
        .pipe(
          tap((stays) => {
            if (stays.length) {
              this.store.dispatch(
                new TimelineUpdateUserStays({
                  stays,
                  userIds,
                  dates: {
                    start: correctedInterval.start.getTime(),
                    end: correctedInterval.end.getTime(),
                  },
                }),
              );
              const stayIds: string[] = stays.map((stay) => stay.id);
              firstValueFrom(this.validateStays(stayIds));
            }
          }),
        );
    } catch (error) {
      console.error(
        'An error occurred while fetching stays or validating them in range:',
        error,
      );

      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
  }

  public validateStays(stayIds: StayId[]): Observable<Record<string, boolean>> {
    try {
      return this.timelineStayGateway.validateStays(stayIds).pipe(
        tap((response) => {
          this.validatedStaysInRangeMap = response;
        }),
      );
    } catch (error) {
      console.error(
        'An error occurred while validating stays in range:',
        error,
      );

      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
  }

  public updateUsersValidatedStays(
    stayIds: StayId[],
  ): Observable<Record<string, boolean>> {
    try {
      return this.timelineStayGateway.validateStays(stayIds).pipe(
        tap((response) => {
          const duplicatedValidatedStaysInRange = this.validatedStaysInRangeMap;
          if (duplicatedValidatedStaysInRange) {
            Object.entries(response).map(
              ([key, value]) => (duplicatedValidatedStaysInRange[key] = value),
            );
          }
          this.validatedStaysInRangeMap = {
            ...duplicatedValidatedStaysInRange,
          };
        }),
      );
    } catch (error) {
      console.error(
        'An error occurred while updating users stays stays in range:',
        error,
      );

      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      throw error;
    }
  }
}
